import { 
  GraduationCap, 
  Mail, 
  MapPin, 
  Phone, 
  Heart,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Shield,
  FileText,
  Scale,
  CreditCard,
  Users,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  BookOpen,
  Video,
  MessageCircle,
  DollarSign,
  Lock,
  Eye,
  Server
} from "lucide-react";
import { Link } from "react-router-dom";

const TermsPage = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2"
            >
              <div className="flex items-center gap-0 font-extrabold">
                <span className="font-bold text-gray-900 text-[1.8rem]">
                  Learn
                </span>
                <span className="font-bold text-orange-500 text-[1.8rem]">
                  Hub
                </span>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-sm text-gray-600 transition-colors hover:text-primary"
              >
                Home
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-gray-600 transition-colors hover:text-primary"
              >
                Privacy
              </Link>
              <Link
                to="/contact"
                className="text-sm text-gray-600 transition-colors hover:text-primary"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl px-4 py-8 mx-auto sm:px-6 lg:px-8 md:py-12">
        {/* Hero Section */}
        <div className="mb-8 text-center md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full mb-4">
            <Scale className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-primary">
              Legal Agreement
            </span>
          </div>
          <h1 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
            Terms of Service
          </h1>
          <p className="max-w-2xl mx-auto text-gray-500">
            Please read these terms carefully before using our platform. They
            govern your relationship with LearnHub.
          </p>
          <p className="flex items-center justify-center gap-1 mt-3 text-sm text-gray-400">
            <Clock className="w-3 h-3" />
            Last updated: April 2026
          </p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-2 gap-3 mb-8 md:grid-cols-4 md:mb-12">
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <Shield className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">
              Binding Agreement
            </p>
            <p className="text-[10px] text-gray-400">Legal Terms</p>
          </div>
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <CreditCard className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">
              Secure Payments
            </p>
            <p className="text-[10px] text-gray-400">Protected Transactions</p>
          </div>
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">
              User Protection
            </p>
            <p className="text-[10px] text-gray-400">Your Rights</p>
          </div>
          <div className="p-3 text-center bg-white border border-gray-200 shadow-sm rounded-xl">
            <FileText className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-xs font-semibold text-gray-900">
              Legal Compliance
            </p>
            <p className="text-[10px] text-gray-400">Fully Compliant</p>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
          {/* Decorative top bar */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-orange-500 to-primary"></div>

          <div className="p-6 md:p-8 lg:p-10">
            <div className="prose-sm prose text-gray-600 max-w-none">
              {/* 1. Acceptance of Terms */}
              <div className="p-4 mb-8 border border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-orange-100 rounded-xl">
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      1. Acceptance of Terms
                    </h2>
                    <p className="text-sm text-gray-600">
                      By accessing or using LearnHub, you agree to be bound by
                      these Terms of Service. If you do not agree to these
                      terms, please do not use our platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* 2. Description of Service */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    2. Description of Service
                  </h2>
                </div>
                <p className="mb-3 text-sm leading-relaxed">
                  LearnHub is an online learning platform that connects students
                  with expert instructors for courses, mentoring sessions, and
                  professional development.
                </p>
                <div className="grid grid-cols-1 gap-2 mt-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <Video className="w-4 h-4 text-primary" />
                    <span>On-demand video courses</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <Users className="w-4 h-4 text-primary" />
                    <span>Live mentoring sessions</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <FileText className="w-4 h-4 text-primary" />
                    <span>Interactive quizzes</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <Award className="w-4 h-4 text-primary" />
                    <span>Certificates of completion</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <MessageCircle className="w-4 h-4 text-primary" />
                    <span>Discussion forums</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span>Progress tracking</span>
                  </div>
                </div>
              </div>

              {/* 3. Use License */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <Lock className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    3. Use License
                  </h2>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="mb-3 text-sm font-medium text-gray-700">
                    You may not:
                  </p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Reproduce or distribute content</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Use platform for illegal purposes</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Attempt unauthorized access</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Transfer your account</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Resell courses without permission</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Record or screenshot content</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. User Accounts */}
              <div className="p-4 mb-8 border border-blue-100 bg-blue-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      4. User Accounts and Registration
                    </h2>
                    <p className="mb-2 text-sm text-gray-600">You agree to:</p>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Provide accurate information</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Maintain account security</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Update information promptly</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Accept responsibility for activities</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Course Content */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    5. Course Content and Intellectual Property
                  </h2>
                </div>
                <p className="mb-3 text-sm text-gray-600">
                  All course content is the intellectual property of LearnHub or
                  its licensors. Unauthorized sharing may result in account
                  termination.
                </p>
                <div className="p-3 border bg-amber-50 rounded-xl border-amber-100">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <AlertCircle className="w-4 h-4" />
                    <span>
                      You may not download, record, or redistribute course
                      content
                    </span>
                  </div>
                </div>
              </div>

              {/* 6. Payments and Refunds */}
              <div className="p-4 mb-8 border border-green-100 bg-green-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-green-100 rounded-xl">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      6. Payments, Pricing, and Refunds
                    </h2>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-600">
                        Refund Policy:
                      </p>
                      <ul className="space-y-1 text-sm text-gray-600">
                        <li className="flex items-center gap-2">
                          • Full refund within 30 days (under 20% completion)
                        </li>
                        <li className="flex items-center gap-2">
                          • Pro-rated refund between 20-50% completion
                        </li>
                        <li className="flex items-center gap-2">
                          • No refunds for courses over 50% complete
                        </li>
                        <li className="flex items-center gap-2">
                          • Mentoring session refunds within 24 hours of booking
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* 7. Mentoring Services */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <Users className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    7. Mentoring Services
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Be punctual and prepared</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span>24-hour notice for rescheduling</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span>No-shows are not refunded</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 rounded-lg bg-gray-50">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>Mentors are independent contractors</span>
                  </div>
                </div>
              </div>

              {/* 8. Prohibited Conduct */}
              <div className="p-4 mb-8 border border-red-100 bg-red-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-red-100 rounded-xl">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="mb-1 text-lg font-semibold text-gray-900">
                      8. Prohibited Conduct
                    </h2>
                    <div className="grid grid-cols-1 gap-1 text-sm text-gray-600 md:grid-cols-2">
                      <span>• Harassment or abuse</span>
                      <span>• Posting illegal content</span>
                      <span>• Attempting to hack the platform</span>
                      <span>• Spamming or advertising</span>
                      <span>• Impersonating others</span>
                      <span>• Sharing account credentials</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 9. User-Generated Content */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <MessageCircle className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    9. User-Generated Content
                  </h2>
                </div>
                <p className="text-sm text-gray-600">
                  By posting reviews, comments, or other content, you grant
                  LearnHub a license to use and display your content, and you
                  represent that you have the rights to post it.
                </p>
              </div>

              {/* 10-13. Legal Sections */}
              <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-gray-800">
                      Disclaimer of Warranties
                    </h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    Platform provided "AS IS" without warranties. We don't
                    guarantee specific outcomes.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-gray-800">
                      Limitation of Liability
                    </h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    Our liability limited to amount you paid. No indirect or
                    consequential damages.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-gray-800">
                      Indemnification
                    </h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    You agree to indemnify LearnHub against claims from your use
                    or violation of terms.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-gray-800">Termination</h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    We may terminate accounts that violate these Terms.
                  </p>
                </div>
              </div>

              {/* 14-18. Final Sections */}
              <div className="mb-8 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10">
                    <Scale className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-sm text-gray-700">
                    <strong>Governing Law:</strong> Laws of Cameroon
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10">
                    <FileText className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-sm text-gray-700">
                    <strong>Changes to Terms:</strong> We'll notify you of
                    material changes
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10">
                    <CheckCircle className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-sm text-gray-700">
                    <strong>Entire Agreement:</strong> These terms supersede all
                    prior agreements
                  </span>
                </div>
              </div>

              {/* Contact Information */}
              <div className="pt-6 mt-8 border-t border-gray-200">
                <div className="p-5 bg-gradient-to-r from-primary/5 to-orange-500/5 rounded-xl">
                  <h2 className="flex items-center gap-2 mb-3 text-lg font-semibold text-gray-900">
                    <Mail className="w-5 h-5 text-primary" />
                    Contact Us
                  </h2>
                  <p className="mb-4 text-sm text-gray-600">
                    Have questions about these Terms? Contact our legal team:
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4 text-primary" />
                      <span>ndzelenromarick@gmail.com</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 text-primary" />
                      <span>+237 676 154 253</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span>Bamenda, Cameroon</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Light Footer */}
      <footer className="mt-12 bg-white border-t border-gray-200">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">© {currentYear}</span>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
              <Link
                to="/privacy"
                className="transition-colors hover:text-primary"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="transition-colors hover:text-primary"
              >
                Terms
              </Link>
              <Link
                to="/contact"
                className="transition-colors hover:text-primary"
              >
                Contact
              </Link>
              <Link
                to="/about"
                className="transition-colors hover:text-primary"
              >
                About
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Missing components
const Award = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="7"></circle>
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
  </svg>
);

const TrendingUp = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
    <polyline points="17 18 23 18 23 12"></polyline>
  </svg>
);

export default TermsPage;